<script lang="ts">
  import type { PaginationInfo } from '../../types/paginate';
  import type {
    GetListParams,
    Item,
    ItemActionClickEvent,
    ProcessedItem,
    SourceWithCount,
  } from '../../modules/item/item.interface';
  import type { CategoryConfig } from '../../modules/projects/project.interface';
  import { createEventDispatcher } from 'svelte';
  import { querystring, location, push } from 'svelte-spa-router';
  import { ItemAction, ItemStatus } from '../../modules/item/item.constants';
  import { ItemService, ProjectConfigService } from '../../modules';
  import { report, wrapProgress, day, sorting } from '../../store';
  import ItemPreview from '../item/ItemPreview.svelte';
  import AppPreview from '../item/AppPreview.svelte';
  import Scroller from '../Scroller.svelte';
  import Pagination from '../Pagination.svelte';
  import dayjs from 'dayjs';
  import type { ListQuery } from '../../types/listQuery';
  import Lazy from 'svelte-lazy';
  import { indexOf } from 'lodash';

  interface KeyboardActions {
    actions: ItemAction[];
    item: Item;
  }

  export let query: ListQuery;
  export let processedItems: ProcessedItem[];
  export let sources: SourceWithCount[];
  export let updatedAt: string;
  export let sourceLastCrawled: string;
  export const fetchListItems = fetchItems;

  const dispatch = createEventDispatcher();
  let resetScroll: () => void;
  let categoryConfig: CategoryConfig;
  let page = 1;
  let limit = 20;
  let paginationInfo: PaginationInfo;
  let items: Item[] = [];
  let keyboardActions: KeyboardActions | null = null;
  let disableLazy = false;
  let totalItems = Infinity;

  $: onQueryChange(query);
  $: isFinished = items.length >= totalItems;

  function onQueryChange(query: ListQuery) {
    categoryConfig = ProjectConfigService.getCategoryConfig(
      $report.project,
      query.category
    );

    page = 1;
    fetchItems(true);
    getUpdatedAt($report.project, query.category, query.source);
    getSource(query.source);
  }

  async function fetchItems(reset?: boolean, scroll?: boolean) {
    return wrapProgress(async () => {
      if (reset) {
        page = 1;
        items = [];
      } else if (isFinished) {
        return;
      }

      const _query = { ...query };

      const params: GetListParams = {
        page,
        limit,
        status: query.status,
        source: query.source,
        category: query.category,
        project: $report.project,
        date: $report.date,
        days: $day,
      };

      if (query.status === ItemStatus.CANDIDATE) {
        params.sort = query.sort;
      }

      try {
        const { docs, ...paginationRes } = await ItemService.getList(params);
        paginationInfo = paginationRes;
        totalItems = paginationRes.totalDocs;

        if (scroll) {
          items = [...items, ...docs];
          let newItems = [];
          items.map((x) =>
            newItems.filter((a) => a.id == x.id && a.title == x.title).length > 0
              ? null
              : newItems.push(x)
          );
          items = newItems;
        } else {
          items = docs;
        }
      } catch (e) {
        console.log(e);
      }

      disableLazy = false;
      // TODO: (Bad practice) A better way to prevent both category & status changes?
      if (JSON.stringify(_query) !== JSON.stringify(query)) return;
      getUpdatedAt($report.project, query.category, query.source);
      getSource(query.source);
    })();
  }

  function getProcess(processedItems: ProcessedItem[], itemId: number) {
    return processedItems.find((pi) => pi.id === itemId);
  }

  function handleItemActions(evt: KeyboardEvent) {
    const numeric = Number(evt.key);

    if (numeric && !isNaN(numeric)) {
      return wrapProgress(async () => {
        await onPageChange({ detail: numeric });
      })();
    }

    if (!keyboardActions) return;

    if (evt.key === 'v') {
      return window.open(keyboardActions.item.url, '_blank');
    }

    if (getProcess(processedItems, keyboardActions.item.id)?.status) {
    }

    const actionMapping: Record<string, ItemAction> = {
      e: ItemAction.EDIT,
      s: ItemAction.SELECT,
      q: ItemAction.QUEUE,
      d: ItemAction.DISMISS,
      u: ItemAction.UNDO,
    };

    const action = actionMapping[evt.key];
    const { actions, item } = keyboardActions;

    if (action === ItemAction.UNDO && getProcess(processedItems, item.id)?.status) {
      dispatch('item-action-click', { action, item });
      return;
    }

    if (!action || !actions.includes(action)) return;

    dispatch('item-action-click', { action, item });
  }

  function onItemHovered(evt: { detail: KeyboardActions | null }) {
    keyboardActions = evt.detail;
  }

  function onItemAction(evt: ItemActionClickEvent) {
    dispatch('item-action-click', evt.detail);
  }

  async function onPageChange(evt: { detail: number }) {
    page = evt.detail;
    resetScroll();
    await fetchItems();
  }

  async function onSortingChange(evt: { detail: string }) {
    const searchParams = new URLSearchParams($querystring);

    sorting.setSelectedSorting(evt.detail);
    searchParams.set('sort', evt.detail);
    resetScroll();

    push(`${$location}?${searchParams}`);
  }

  function getSource(sourceKey: string) {
    let selectedSource = sources.find((source) => source.key === sourceKey);
    sourceLastCrawled = selectedSource?.lastCrawled;
    return selectedSource;
  }

  async function getUpdatedAt(project: string, category: string, source: string) {
    const sourceUpdatedAt: any = await ItemService.getUpdatedAt(
      project,
      category,
      source
    );
    updatedAt = dayjs(sourceUpdatedAt).fromNow();
  }

  function onEndReached() {
    fetchItems(false, true);
    page++;
    disableLazy = true;
  }
</script>

<svelte:window on:keydown={handleItemActions} />
<div class="ril-wrapper">
  <Scroller bind:resetScroll>
    <ul class="ri-list {categoryConfig.displayType}">
      {#each items as item (item.id)}
        <li
          class={['mobbinflow', 'mobbinios', 'mobbinweb'].indexOf(item.sourceKey) >= 0
            ? 'app-preview'
            : ''}
        >
          {#if ['mobbinflow', 'mobbinios', 'mobbinweb'].indexOf(item.sourceKey) >= 0}
            <AppPreview
              bind:item
              process={getProcess(processedItems, item.id)}
              {categoryConfig}
              actions={true}
              contentDisplayType={item.sourceKey === 'mobbinflow'
                ? 'SINGLE_VIDEO'
                : item.sourceKey === 'mobbinios'
                ? 'MULTI_IMAGE'
                : item.sourceKey === 'mobbinweb'
                ? 'SINGLE_IMAGE'
                : null}
              source={!query.source ? getSource(item.sourceKey) : null}
              on:action={onItemAction}
              on:hovered-item={onItemHovered}
            />
          {:else}
            <ItemPreview
              bind:item
              process={getProcess(processedItems, item.id)}
              {categoryConfig}
              actions={true}
              source={!query.source ? getSource(item.sourceKey) : null}
              on:action={onItemAction}
              on:hovered-item={onItemHovered}
            />
          {/if}
        </li>
      {/each}
    </ul>
    {#if !disableLazy}
      <Lazy offset={500} onload={onEndReached}>Loading...</Lazy>
    {/if}
  </Scroller>
  <Pagination
    {paginationInfo}
    {categoryConfig}
    sort={query.sort}
    on:page-change={wrapProgress(onPageChange)}
    on:sorting-change={onSortingChange}
  />
</div>

<style lang="scss">
  .ril-wrapper {
    display: flex;
    flex: 1;
    flex-direction: column;
    height: 100%;
    overflow: hidden;
  }

  .ri-list {
    --item-gap: calc(var(--gap) * 3);
    padding-left: var(--gap);
    padding-top: 6px;

    li {
      width: calc(50% - var(--item-gap));
      min-width: 250px;
      max-width: 350px;
      display: inline-block;
      margin: 0 calc(var(--item-gap) / 2);
      vertical-align: top;

      &.app-preview {
        max-width: 400px;
      }
    }

    &.tweet {
      li {
        width: 600px;
        margin: 0 calc(var(--item-gap) / 2);
        clear: both;
        min-width: 0;
        max-width: none;
        float: left;
      }
    }

    &.text {
      max-width: 376px;
      padding-left: var(--item-gap);

      li {
        width: 100%;
        max-width: 100%;
        margin: 0 0 24px;
      }
    }
  }

  :global(.ril-wrapper .scroller) {
    flex: 1;
  }
</style>
